import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Checkbox, Flex, Loader, NumberInput, ScrollArea, Select, Stack, TextInput, Title } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { AppDrawer } from "../../shared/AppDrawer";
import { playwright_location_drawer_address_1_input, playwright_location_drawer_address_2_input, playwright_location_drawer_city_input, playwright_location_drawer_create_update_button, playwright_location_drawer_is_primary_checkbox, playwright_location_drawer_latitude_input, playwright_location_drawer_longitude_input, playwright_location_drawer_name_input, playwright_location_drawer_postal_code_input, playwright_location_drawer_state_input, playwright_location_drawer_state_option, playwright_presented_drawer_parent } from "@therapprove/playwright";
import { useEffect, useState } from "react";
import { STATES } from "../../datasets/states";
import { useCreateLocationMutation, useGetLocationQuery, useGetPostalCodeQuery, useUpdateLocationMutation } from "../../graphql/graphql";
import { getFormSchemaData } from "../../utils/Form.utils";
import { removeNullValues } from "../../utils/Object.utils";
import { titleCase } from "../../utils/String.utils";
import { Length } from "../../utils/Validation.utils";
import { Confirmation } from "../../shared/Confirmation";
import { showErrorNotification, showSuccessNotification } from "../../shared/Notifications";
import { LocationInitialValues, LocationSchema } from "./LocationDrawer.validation";
export const LocationDrawer = ({ organizationId, locationId, options: { allowEdit = false } = {}, opened, close }) => {
    // State
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    // Form
    const form = useForm({
        mode: "controlled",
        initialValues: LocationInitialValues,
        validate: yupResolver(LocationSchema)
    });
    // Queries
    const [{ data: locationData, fetching: fetchingLocation }] = useGetLocationQuery({
        variables: {
            organizationId: organizationId,
            locationId: locationId || ""
        },
        pause: !locationId
    });
    // Mutations
    const [{ fetching: createFetching }, createLocation] = useCreateLocationMutation();
    const [{ fetching: updateFetching }, updateLocation] = useUpdateLocationMutation();
    const fetching = createFetching || updateFetching || fetchingLocation;
    // Fetch the city and state when the postal code is changed
    const [{ data: postalCodeData, fetching: fetchingPostalCode }] = useGetPostalCodeQuery({
        variables: {
            postalCode: form.values.postalCode || ""
        },
        pause: form.values.postalCode.length < Length.PostalCode
    });
    useEffect(() => {
        if (!locationId) {
            form.setValues(LocationInitialValues);
        }
        else {
            const location = locationData?.locations?.location;
            if (location) {
                form.setValues(getFormSchemaData(LocationSchema, location));
            }
        }
        form.resetDirty();
    }, [opened, locationId, locationData]);
    useEffect(() => {
        if (postalCodeData?.postalCodes?.postalCode) {
            form.setValues({
                ...form.values,
                city: postalCodeData.postalCodes.postalCode.city || "",
                stateName: postalCodeData.postalCodes.postalCode.stateName || ""
            });
        }
        else {
            form.setValues({
                ...form.values,
                city: "",
                stateName: ""
            });
        }
    }, [postalCodeData]);
    // Submits the Location entity to be created or updated
    const submitLocation = async (event) => {
        event.preventDefault();
        const valid = form.validate();
        if (valid.hasErrors)
            return;
        const formValues = form.getValues();
        removeNullValues(formValues);
        try {
            let result;
            if (locationId) {
                const updateResponse = await updateLocation({
                    ...formValues,
                    locationId: locationId || ""
                });
                result = updateResponse.data?.locations
                    ?.updateLocation;
            }
            else {
                const createResponse = await createLocation(formValues);
                result = createResponse.data?.locations
                    ?.createLocation;
            }
            if (!result) {
                throw new Error(`Failed to ${locationId ? "update" : "create"} location.`);
            }
            showSuccessNotification(`Location was ${locationId ? "updated" : "created"} successfully!`);
            // Reset form dirty state after successful submission
            form.resetDirty();
            handleClose();
        }
        catch (error) {
            console.error("An error occurred.", error);
            showErrorNotification();
        }
    };
    const handleClose = (shouldConfirmLeave = true) => {
        if (form.isDirty() && shouldConfirmLeave) {
            setIsConfirmationModalOpen(true);
            return;
        }
        setIsConfirmationModalOpen(false);
        form.reset();
        close();
    };
    return (_jsxs(AppDrawer, { title: _jsxs(Flex, { align: "center", gap: "xs", children: [_jsx(Title, { order: 4, children: `${locationId ? "Update" : "Add"} Location` }), fetching && _jsx(Loader, { size: 16 })] }), scrollAreaComponent: ScrollArea.Autosize, opened: opened, onClose: handleClose, stackId: "location", "data-testid": playwright_presented_drawer_parent, children: [_jsxs(Stack, { gap: "sm", children: [_jsx(TextInput, { autoFocus: true, required: true, label: "Location Name", placeholder: "Headquaters, Main Office, etc...", ...form.getInputProps("name"), "data-testid": playwright_location_drawer_name_input }), _jsx(TextInput, { label: "Address Line 1", placeholder: "Address Line 1", ...form.getInputProps("address1"), "data-testid": playwright_location_drawer_address_1_input }), _jsx(TextInput, { label: "Address Line 2", placeholder: "Address Line 2", ...form.getInputProps("address2"), "data-testid": playwright_location_drawer_address_2_input }), _jsxs(Flex, { gap: "xs", children: [_jsx(TextInput, { label: "City", placeholder: "City", rightSection: fetchingPostalCode ? _jsx(Loader, { size: 16 }) : null, ...form.getInputProps("city"), value: titleCase(form.values.city), "data-testid": playwright_location_drawer_city_input }), _jsx(Select, { checkIconPosition: "right", label: "State", placeholder: "State", rightSection: fetchingPostalCode ? _jsx(Loader, { size: 16 }) : null, data: STATES.map((state) => ({
                                    value: state,
                                    label: state,
                                    "data-testid": playwright_location_drawer_state_option
                                })), clearable: true, searchable: true, nothingFoundMessage: "No state found...", ...form.getInputProps("stateName"), value: titleCase(form.values.stateName), "data-testid": playwright_location_drawer_state_input }), _jsx(TextInput, { label: "Zip Code", withAsterisk: true, placeholder: "Zip Code", ...form.getInputProps("postalCode"), onChange: (event) => {
                                    form.setFieldValue("postalCode", event.currentTarget.value);
                                }, "data-testid": playwright_location_drawer_postal_code_input })] }), _jsxs(Flex, { gap: "xs", children: [_jsx(NumberInput, { label: "Latitude", placeholder: "Latitude", hideControls: true, min: -90, max: 90, clampBehavior: "strict", allowNegative: true, allowDecimal: true, decimalScale: 7, ...form.getInputProps("latitude"), onChange: (value) => {
                                    form.setFieldValue("latitude", Number(value));
                                }, "data-testid": playwright_location_drawer_latitude_input }), _jsx(NumberInput, { label: "Longitude", placeholder: "Longitude", hideControls: true, min: -180, max: 180, clampBehavior: "strict", allowNegative: true, allowDecimal: true, decimalScale: 7, ...form.getInputProps("longitude"), onChange: (value) => {
                                    form.setFieldValue("longitude", Number(value));
                                }, "data-testid": playwright_location_drawer_longitude_input })] }), _jsx(Checkbox, { label: "Is Primary Location?", checked: form.values.isPrimary, mt: "xs", onChange: (event) => {
                            form.setFieldValue("isPrimary", event.currentTarget.checked);
                        }, "data-testid": playwright_location_drawer_is_primary_checkbox })] }), (allowEdit || !locationId) && (_jsx(Button, { variant: "filled", mt: "3rem", loading: fetching, onClick: submitLocation, "data-testid": playwright_location_drawer_create_update_button, children: locationId ? "Update" : "Create" })), _jsx(Confirmation, { action: "leave", additionalText: "You have unsaved changes, that won't be saved.", opened: isConfirmationModalOpen, close: () => setIsConfirmationModalOpen(false), handleAction: async () => {
                    handleClose(false);
                } })] }));
};
